import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetOperationTemplateResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

const STALE_TIME_MS = 5 * 60_000;

/**
 * One template in full — every version summary, plus the lines of the version in
 * force and of the draft.
 *
 * Keyed on the template's `key`, which is also what the URL carries, so opening
 * the editor for a second operation is a different query rather than a refetch
 * of the first.
 *
 * `enabled` is the caller's, for the reason the catalog's is: access is derived
 * from the session before fetching, because a PIN/kiosk token is refused by
 * every accounting endpoint whatever role it holds and a raw 403 tells the user
 * nothing they can act on.
 */
export const useOperationTemplateQuery = (key: string, enabled: boolean) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetOperationTemplateResponse>>({
        queryKey: OperationsQueryKeys.template(key),
        queryFn: () => apiClient.operations.getTemplate(key),
        enabled: enabled && key.length > 0,
        staleTime: STALE_TIME_MS,
    });
};
